import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import InfiniteScroll from "react-infinite-scroll-component";
import toast from "react-hot-toast";
import { FiInfo, FiFilter, FiUser } from "react-icons/fi";
import { FaCheck, FaTimes } from "react-icons/fa";
import * as routes from "../../routes";
import { CommonTable, Loader } from "../../Components/Common";
import CommonFilter from "../../Components/Common/CommonFilter";
import { GET_ALL_PAGINATION, RESET_PAGINATION } from "../../Constants/Strings";
import { extractErrorMessage } from "../../Utils/extractErrorMessage";
import {
  approveLeaves,
  getLeaveApprovals,
  getLeaveUsers,
} from "../../Services/LeaveServices";
import {
  LEAVE_CATEGORY_OPTIONS,
  LEAVE_REQUEST_COLUMN_LABELS,
  LEAVE_REQUEST_STATUS,
  LEAVE_REQUEST_STATUS_OPTIONS,
  LEAVE_REQUEST_TABLE_COLUMNS,
  TOAST_MESSAGES,
} from "./constants";
import { formatDateDDMMYYYY, toProperCase } from "../../Utils";

const LeaveApprovals = () => {
  const [LeaveRequest, setLeaveRequest] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState(RESET_PAGINATION);
  const [users, setUsers] = useState([]);
  const location = useLocation();
  const [filters, setFilters] = useState(() => {
    return (
      location.state?.filters || {
        status: "",
        category: "",
        user: "",
      }
    );
  });
  const [selectedUserOption, setSelectedUserOption] = useState(() => {
    return location.state?.selectedUser || null;
  });
  const [tempFilters, setTempFilters] = useState(filters);
  const [showFilter, setShowFilter] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchLeaveRequest({ offset: 0, limit: pagination.limit }, true, filters);
  }, [filters]);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const res = (await getLeaveUsers(GET_ALL_PAGINATION)).data;
        setUsers(res);
      } catch (err) {
        const msg = extractErrorMessage(err) || TOAST_MESSAGES.USER_FETCH_ERROR;
        toast.error(msg);
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, []);

  const fetchLeaveRequest = async (
    params,
    isInitial = false,
    currentFilters = filters,
  ) => {
    setLoading(true);
    try {
      const cleanedFilters = Object.fromEntries(
        Object.entries(currentFilters).filter(([_, val]) => val !== ""),
      );

      const res = await getLeaveApprovals({ ...params, ...cleanedFilters });

      if (!res || !res.data) {
        throw new Error(TOAST_MESSAGES.NO_RESPONSE_DATA);
      }

      const results = res.data.results || [];
      setLeaveRequest((prev) => (isInitial ? results : [...prev, ...results]));
      setPagination((prev) => ({
        ...prev,
        count: res.data.count || 0,
        offset: isInitial ? results.length : prev.offset + results.length,
      }));
    } catch (err) {
      toast.error(TOAST_MESSAGES.FAILED_FETCH_LEAVES);
      if (isInitial) {
        setLeaveRequest([]);
        setPagination((prev) => ({ ...prev, count: 0, offset: 0 }));
      }
    } finally {
      setLoading(false);
    }
  };

  const loadMoreLeaveRequest = () => {
    if (pagination.offset >= pagination.count) return;
    fetchLeaveRequest({ offset: pagination.offset, limit: pagination.limit });
  };

  const handleStatusChange = async (id, status) => {
    try {
      const response = await approveLeaves(id, { status: status });
      if (response.status === 200) {
        toast.success(TOAST_MESSAGES.LEAVE_UPDATE_SUCCESS);
        setPagination(RESET_PAGINATION);
        fetchLeaveRequest({ offset: 0, limit: RESET_PAGINATION.limit }, true);
      } else {
        const msg =
          extractErrorMessage(response) || TOAST_MESSAGES.APPROVAL_FAILED;
        toast.error(msg);
      }
    } catch (error) {
      const msg = extractErrorMessage(error) || TOAST_MESSAGES.APPROVAL_FAILED;
      toast.error(msg);
    }
  };

  const handleApplyFilters = () => {
    if (
      tempFilters.status === "" &&
      tempFilters.user === "" &&
      tempFilters.category === ""
    ) {
      return;
    }
    setSelectedUserOption(tempFilters.user);
    tempFilters.user = tempFilters.user?.id;
    setFilters(tempFilters);
    setPagination(RESET_PAGINATION);
    setLeaveRequest([]);
    setShowFilter(false);

    // Update navigation state for back button
    navigate(".", {
      state: { filters: tempFilters, selectedUser: tempFilters.user },
      replace: true,
    });
  };

  const handleClearFilters = () => {
    const cleared = { status: "", user: "", category: "" };
    setTempFilters(cleared);
    setFilters(cleared);
    setPagination(RESET_PAGINATION);
    setLeaveRequest([]);
    setSelectedUserOption(null);

    navigate(".", {
      state: { filters: cleared, selectedUser: null },
      replace: true,
    });
  };

  const handleCancelFilters = () => {
    setTempFilters(filters);
    setShowFilter(false);
  };

  const handleRenderField = (key, row) => {
    const isPending =
      row.status === LEAVE_REQUEST_STATUS.PENDING_L1_APPROVAL ||
      LEAVE_REQUEST_STATUS.PENDING_L2_APPROVAL;
    if (key === "created") {
          return row.created
            ? new Date(row.created).toLocaleString("en-IN", {
                day: "2-digit",
                month: "short",
                year: "numeric",
              })
            : "-";
        }
        if (key === "is_half_day") {
      return row.is_half_day ? (
        <FaCheck className="text-green-600" />
      ) : (
        null
      );
    }
    if (key === "category") return toProperCase(row.category) + " Leave" || "-";
    if (key === "status") return toProperCase(row.status);
    if (key === "start_date") return formatDateDDMMYYYY(row.start_date);
    if (key === "end_date") return formatDateDDMMYYYY(row.end_date);
    if (key === "user") return row.user_name || "-";
    if (key === "actions") {
      return (
        <div
          className={`grid grid-cols-3 items-center justify-center gap-4 text-xl text-center`}
        >
          <FiInfo
            className="text-green-600 text-xl cursor-pointer"
            onClick={() =>
              navigate(
                routes.VIEW_LEAVE_APPROVALS.replace(":leaveRequestId", row.id),
                {
                  state: { filters, fromPage: routes.LEAVE_APPROVALS },
                },
              )
            }
            title={"View Details"}
          />
          <>
            <FaCheck
              className={`cursor-pointer ${
                isPending
                  ? "text-green-600"
                  : "text-gray-400 cursor-not-allowed"
              }`}
              onClick={() =>
                isPending &&
                handleStatusChange(
                  row.id,
                  row.status === LEAVE_REQUEST_STATUS.PENDING_L1_APPROVAL
                    ? LEAVE_REQUEST_STATUS.PENDING_L2_APPROVAL
                    : LEAVE_REQUEST_STATUS.APPROVED,
                )
              }
              title={"Approve"}
            />
            <FaTimes
              className={`cursor-pointer ${
                isPending ? "text-red-600" : "text-gray-400 cursor-not-allowed"
              }`}
              onClick={() =>
                isPending &&
                handleStatusChange(row.id, LEAVE_REQUEST_STATUS.REJECTED)
              }
              title={"Reject"}
            />
          </>
        </div>
      );
    }
    return row[key] || "-";
  };

  return (
    <div className="p-4">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold text-primary">Leave Approvals</h2>

        <div className="flex gap-2 items-center">
          <button
            className="px-4 py-2 rounded bg-primary hover:bg-green-600 text-white font-bold shadow-lg flex items-center gap-1"
            onClick={() => {
              setTempFilters(filters);
              setShowFilter(true);
            }}
          >
            <FiFilter className="text-xl" />
          </button>
        </div>
      </div>

      {/* Filter Modal */}
      {showFilter && (
        <CommonFilter
          title="Leave Approval Filter"
          tempFilters={tempFilters}
          setTempFilters={setTempFilters}
          onApply={handleApplyFilters}
          onClear={handleClearFilters}
          onCancel={handleCancelFilters}
          userOptions={users}
          categoryOptions={[
            { label: "All", value: "" },
            ...LEAVE_CATEGORY_OPTIONS,
          ]}
          statusOptions={LEAVE_REQUEST_STATUS_OPTIONS}
          useSearchableUser={true}
          selectedSearchableValue={selectedUserOption}
        />
      )}

      {/* Loader or Data */}
      {loading && LeaveRequest.length === 0 ? (
        <div className="flex justify-center pt-10">
          <Loader size={4} />
        </div>
      ) : LeaveRequest.length === 0 ? (
        <div className="text-center text-gray-500 mt-10">
          No Leaves Available
        </div>
      ) : (
        <>
          {/* Desktop View */}
          <div className="hidden md:block">
            <InfiniteScroll
              dataLength={LeaveRequest.length}
              next={loadMoreLeaveRequest}
              hasMore={pagination.offset < pagination.count}
              scrollableTarget="desktopPendingLeaves"
            >
              <div
                id="desktopPendingLeaves"
                className="h-[68vh] overflow-y-auto border rounded-md"
              >
                <CommonTable
                  tableColumns={LEAVE_REQUEST_TABLE_COLUMNS}
                  tableRows={LeaveRequest}
                  columnLabels={LEAVE_REQUEST_COLUMN_LABELS}
                  renderField={handleRenderField}
                />
              </div>
            </InfiniteScroll>
          </div>
        </>
      )}

      {/* MOBILE VIEW (Card UI) */}
      <div className="md:hidden">
        <InfiniteScroll
          dataLength={LeaveRequest.length}
          next={loadMoreLeaveRequest}
          hasMore={pagination.offset < pagination.count}
          scrollableTarget="mobilePendingLeaves"
        >
          <div
            id="mobilePendingLeaves"
            className="h-[75vh] overflow-y-auto space-y-2 p-1"
          >
            {LeaveRequest.map((item) => {
              const isPending =
                item.status === LEAVE_REQUEST_STATUS.PENDING_L1_APPROVAL ||
                LEAVE_REQUEST_STATUS.PENDING_L2_APPROVAL;
              const displayDate =
                item.start_date === item.end_date ? true : false;
              return (
                <div
                  key={item.id}
                  className="bg-white shadow-md rounded-lg p-2 border border-gray-200"
                  onClick={() =>
                    navigate(
                      routes.VIEW_LEAVE_APPROVALS.replace(
                        ":leaveRequestId",
                        item.id,
                      ),
                      {
                        state: { filters, fromPage: routes.LEAVE_APPROVALS },
                      },
                    )
                  }
                >
                  {/* Header */}
                  <div className="flex justify-between items-center mb-2">
                    <h3 className="font-semibold text-lg text-gray-800 truncate">
                      {toProperCase(item.category) + " Leave" || "-"}
                    </h3>

                    <span
                      className={`py-1 rounded-full text-sm font-semibold 
                                 ${
                                   item.status === "PENDING_APPROVAL"
                                     ? "text-yellow-700"
                                     : item.status === "APPROVED"
                                       ? " text-green-700"
                                       : item.status === "REJECTED"
                                         ? " text-red-700"
                                         : item.status === "CANCELLED"
                                           ? " text-gray-700"
                                           : " text-gray-600"
                                 }`}
                    >
                      {toProperCase(item.status)}
                    </span>
                  </div>

                  <div className="flex justify-between">
                    <div>
                      {/* Dates */}
                      <div className="flex gap-1 mb-1">
                        <div className="flex justify-between">
                          <p className="text-sm">
                            {formatDateDDMMYYYY(item.start_date)}
                          </p>
                          <div></div>
                        </div>

                        {!displayDate && (
                          <div className="flex justify-between">
                            <p className="text-sm">
                              to {formatDateDDMMYYYY(item.end_date)}
                            </p>
                            <div></div>
                          </div>
                        )}
                      </div>

                      <p className="flex gap-1 items-center text-sm">
                        <FiUser size={18} /> {item.user_name || "-"}{" "}
                      </p>
                    </div>

                    {/* action button */}
                    <div className="flex flex-col justify-between items-center">
                      {/** CANCEL BUTTON **/}
                      {isPending && (
                        <>
                          <FaCheck
                            className={`cursor-pointer ${
                              isPending
                                ? "text-green-600"
                                : "text-gray-400 cursor-not-allowed"
                            }`}
                            onClick={(e) => {
                              e.stopPropagation();
                              isPending &&
                                handleStatusChange(
                                  item.id,
                                  item.status ===
                                    LEAVE_REQUEST_STATUS.PENDING_L1_APPROVAL
                                    ? LEAVE_REQUEST_STATUS.PENDING_L2_APPROVAL
                                    : LEAVE_REQUEST_STATUS.APPROVED,
                                );
                            }}
                            title={"Approve"}
                          />
                          <FaTimes
                            className={`cursor-pointer ${
                              isPending
                                ? "text-red-600"
                                : "text-gray-400 cursor-not-allowed"
                            }`}
                            onClick={(e) => {
                              e.stopPropagation();
                              isPending &&
                                handleStatusChange(
                                  item.id,
                                  LEAVE_REQUEST_STATUS.REJECTED,
                                );
                            }}
                            title={"Reject"}
                          />
                        </>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </InfiniteScroll>
      </div>
    </div>
  );
};

export default LeaveApprovals;
